import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleDown, faArrowAltCircleUp } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  Table,
  Select,
  Input,
  Spin
} from 'antd';
import { notificationsAtom, userPrivilagesAtom } from '../../../atoms/Atoms';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import { thousand, } from '../../../functions';

function CloverSalesSummary() {
  const { t } = useTranslation();
  const { Option } = Select;
  const sortFilters = [
    { name: `${t('name')} Z-A`, type: 0 },
    { name: `${t('name')} A-Z`, type: 1 },
    { name: `${t('category')} Z-A`, type: 2 },
    { name: `${t('category')} A-Z`, type: 3 },
    { name: `${t('this_week_sales')} ${t('desc')}`, type: 4 },
    { name: `${t('this_week_sales')} ${t('asc')}`, type: 5 },
    { name: `${t('this_week_avg')} ${t('desc')}`, type: 6 },
    { name: `${t('this_week_avg')} ${t('asc')}`, type: 7 },
    { name: `${t('one_week_ago_sales')} ${t('desc')}`, type: 8 },
    { name: `${t('one_week_ago_sales')} ${t('asc')}`, type: 9 },
    { name: `${t('one_week_ago_avg')} ${t('desc')}`, type: 10 },
    { name: `${t('one_week_ago_avg')} ${t('asc')}`, type: 11 },
    { name: `${t('two_week_ago_sales')} ${t('desc')}`, type: 12 },
    { name: `${t('two_week_ago_sales')} ${t('asc')}`, type: 13 },
    { name: `${t('two_week_ago_avg')} ${t('desc')}`, type: 14 },
    { name: `${t('two_week_ago_avg')} ${t('asc')}`, type: 15 },
    { name: `${t('three_week_ago_sales')} ${t('desc')}`, type: 16 },
    { name: `${t('three_week_ago_sales')} ${t('asc')}`, type: 17 },
    { name: `${t('three_week_ago_avg')} ${t('desc')}`, type: 18 },
    { name: `${t('three_week_ago_avg')} ${t('asc')}`, type: 19 },
    { name: `${t('four_week_ago_sales')} ${t('desc')}`, type: 20 },
    { name: `${t('four_week_ago_sales')} ${t('asc')}`, type: 21 },
    { name: `${t('four_week_ago_avg')} ${t('desc')}`, type: 22 },
    { name: `${t('four_week_ago_avg')} ${t('asc')}`, type: 23 },
  ];
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [loading, setLoading] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categories, setCategories] = useState([]);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedSortFilter, setSelectedSortFilter] = useState(4);
  const [selectedCategory, setSelectedCategory] = useState(null);

  function getCategories() {
    setLoadingCategories(true);
    api.get('window/summaries/clover/sales/cateogries')
      .then((response) => {
        setLoadingCategories(false);
        if (response.data.success) {
          setCategories(response.data.data);
          if (selectedCategory == null) {
            setSelectedCategory(_.filter(response.data.data, (c) => c)[0]);
          }
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingCategories(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  function search(value, page) {
    if (selectedCategory == null) {
      return;
    }
    setLastSearchTerm(value);
    setCurrentPage(page);
    setLoading(true);
    api
      .post(
        'window/summaries/clover/sales/search',
        {
          Query: value,
          Size: pageSize,
          Page: page,
          Sort: selectedSortFilter,
          Category: selectedCategory
        }
      )
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          setSearchResult(response.data.data);
        } else {
          setSearchResult({
            ...searchResult,
            items: []
          });
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response.data.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function onSearch(value) {
    search(value, 0);
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      search(lastSearchTerm, page - 1);
    } else {
      setPageSize(size);
    }
  };

  useEffect(() => {
    search(lastSearchTerm, 0);
  }, [selectedSortFilter, selectedCategory, pageSize]);

  useEffect(() => {
    setSelectedSortFilter(4);
    getCategories();
  }, []);

  return (
    <MainPage>
      <div className="flex mt-4">
        <H1 text={t('clover_sales_summary')} />
      </div>
      <div className="lg:flex items-center justify-center">
        <Input.Search
          placeholder={`${t('search')}...`}
          allowClear
          disabled={loadingCategories}
          className="mt-4"
          enterButton={t('search')}
          size="large"
          onClear={() => onSearch('')}
          onSearch={(val) => onSearch(val)}
        />
        <div className="grid grid-cols-2 md:grid-cols-4 md:space-x-2 lg:flex">
          <Select
            loading={loadingCategories}
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 mr-1 md:mr-0 ml-0 lg:ml-2"
            value={selectedCategory}
            disabled={loadingCategories}
            onChange={(value) => setSelectedCategory(value)}
          >
            {/* <Option
              key="nocategory"
              value=""
              className="text-sm flex items-center text-start"
            >
              {t('any_category')}
            </Option> */}
            {_.map(categories, (category) => (
              <Option
                key={category ?? '[no_data]'}
                value={category ?? '[no_data]'}
                className="text-sm flex items-center text-start"
              >
                {category ?? t('[no_data]')}
              </Option>
            ))}
          </Select>
          <Select
            className="h-10 col-span-1 mt-2 lg:mt-4 lg:w-48 ml-1 md:ml-0"
            value={selectedSortFilter}
            disabled={loadingCategories}
            onChange={(value) => setSelectedSortFilter(value)}
          >
            {_.map(sortFilters, (filter) => (
              <Option
                key={filter.type}
                value={filter.type}
                className="text-sm flex items-center text-start"
              >
                {filter.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="mt-4">{t('found_in_search')}: {searchResult?.items ? thousand(searchResult?.total) : 0}</div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        loading={loading}
        rowKey="itemCode"
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: searchResult?.total,
          onChange: onTableChange
        }}
        dataSource={searchResult?.items}
        columns={[
          {
            title: t('code'),
            key: 'itemCode',
            align: 'left',
            className: 'text-sm font-semibold text-gray-800',
            render: (row) => (
              <div className="flex space-x-2"><span>{row.itemCode}</span></div>
            ),
          },
          {
            title: t('product'),
            key: 'name',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.name}</span>
            ),
          },
          {
            title: t('four_weeks_ago'),
            key: 'salesFourWeeksAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span className="lowercase">{thousand(row.salesFourWeeksAgo)} ≈ {row.avgFourWeeksAgo?.toFixed(0)} {t('daily')}</span>
            ),
          },
          {
            title: t('three_weeks_ago'),
            key: 'salesThreeWeeksAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.salesThreeWeeksAgo > row.salesFourWeeksAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.salesThreeWeeksAgo)} ≈ {row.avgThreeWeeksAgo?.toFixed(0)} {t('daily')}</span>
              </div>
            ),
          },
          {
            title: t('two_weeks_ago'),
            key: 'salesTwoWeeksAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.salesTwoWeeksAgo > row.salesThreeWeeksAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.salesTwoWeeksAgo)} ≈ {row.avgTwoWeeksAgo?.toFixed(0)} {t('daily')}</span>
              </div>
            ),
          },
          {
            title: t('one_weeks_ago'),
            key: 'salesOneWeekAgo',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{row.salesOneWeekAgo > row.salesTwoWeeksAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                <span className="lowercase"> {thousand(row.salesOneWeekAgo)} ≈ {row.avgOneWeekAgo?.toFixed(0)} {t('daily')}</span>
              </div>
            ),
          },
          {
            title: t('this_week'),
            key: 'salesThisWeek',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div>
                <span>{thousand(row.thisWeek)}</span>
              </div>
            ),
          },
        ]}
        expandable={{
          expandRowByClick: false,
          rowExpandable: (record) => true,
          onExpand: (expanded, record) => {
            if (expanded && !record.loadedStores && !record.loadingStores) {
              const temp = { ...searchResult };
              for (let i = 0; i < temp.items.length; i++) {
                if (temp.items[i].itemCode === record.itemCode) {
                  temp.items[i].loadingStores = true;
                }
              }
              setSearchResult(temp);
              api.get(`window/summaries/clover/sales/bystore?itemCode=${record.itemCode}`)
                .then((response) => {
                  const temp2 = { ...searchResult };
                  if (response.data.success) {
                    for (let i = 0; i < temp2.items.length; i++) {
                      if (temp2.items[i].itemCode === record.itemCode) {
                        temp2.items[i].stores = response.data.data;
                      }
                    }  
                  } else {
                    setNotifications([
                      ...notifications,
                      {
                        title: t('error'),
                        description: response.data.error,
                        error: true,
                      },
                    ]);
                  }
                  for (let i = 0; i < temp2.items.length; i++) {
                    if (temp2.items[i].itemCode === record.itemCode) {
                      temp2.items[i].loadingStores = false;
                    }
                  }
                  console.log(temp2);
                  setSearchResult(temp2);
                })
                .catch((error) => {
                  const temp2 = { ...searchResult };
                  for (let i = 0; i < temp2.items.length; i++) {
                    if (temp2.items[i].itemCode === record.itemCode) {
                      temp2.items[i].loadingStores = false;
                    }
                  }
                  setSearchResult(temp2);
                  setNotifications([
                    ...notifications,
                    {
                      title: t('error'),
                      description: error.message,
                      error: true,
                    },
                  ]);
                });
            }
          },
          expandedRowRender: (record) => (
            <Spin spinning={false}>
              <div className="w-full mb-8">
                <Table
                  loading={record.loadingStores}
                  dataSource={record?.stores}
                  columns={[
                    {
                      title: t('store'),
                      key: 'store',
                      align: 'left',
                      className: 'text-sm font-semibold text-gray-800 w-1/3',
                      render: (row) => (
                        <div className="flex space-x-2"><span>{row.storeName}</span></div>
                      ),
                    },
                    {
                      title: t('four_weeks_ago'),
                      key: 'salesFourWeeksAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <span className="lowercase">{thousand(row.salesFourWeeksAgo)} ≈ {row.avgFourWeeksAgo?.toFixed(0)} {t('daily')}</span>
                      ),
                    },
                    {
                      title: t('three_weeks_ago'),
                      key: 'salesThreeWeeksAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesThreeWeeksAgo > row.salesFourWeeksAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesThreeWeeksAgo)} ≈ {row.avgThreeWeeksAgo?.toFixed(0)} {t('daily')}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('two_weeks_ago'),
                      key: 'salesTwoWeeksAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesTwoWeeksAgo > row.salesThreeWeeksAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesTwoWeeksAgo)} ≈ {row.avgTwoWeeksAgo?.toFixed(0)} {t('daily')}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('one_weeks_ago'),
                      key: 'salesOneWeekAgo',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{row.salesOneWeekAgo > row.salesTwoWeeksAgo ? <FontAwesomeIcon className="text-green-400" icon={faArrowAltCircleUp} /> : <FontAwesomeIcon className="text-red-400" icon={faArrowAltCircleDown} />}</span>
                          <span className="lowercase"> {thousand(row.salesOneWeekAgo)} ≈ {row.avgOneWeekAgo?.toFixed(0)} {t('daily')}</span>
                        </div>
                      ),
                    },
                    {
                      title: t('this_week'),
                      key: 'salesThisWeek',
                      align: 'left',
                      className: 'text-sm',
                      render: (row) => (
                        <div>
                          <span>{thousand(row.thisWeek)}</span>
                        </div>
                      ),
                    },
                    // {
                    //   title: t('grouped_by_store'),
                    //   className: 'text-sm',
                    //   render: (row) => (
                    //     <div className="space-y-2">
                    //       <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    //         <div className="flex space-x-2"><strong>{t('store')}:</strong> <span>{row.storeName}</span></div>
                    //       </div>
                    //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    //         <Card
                    //           type="inner"
                    //           title={t('this_week')}
                    //           className="rounded-xl col-span-2 lg:col-span-1"
                    //           hoverable
                    //           loading={loading}
                    //         >
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomLeftRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('sales')}:</span>
                    //             <span> {thousand(row.thisWeek)}</span>
                    //           </Card.Grid>
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               bottomRowGridStyle,
                    //               borderBottomRightRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('sales')}:</span>
                    //             <span> {thousand(row.thisWeek)}</span>
                    //           </Card.Grid>
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomRightRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('average')}:</span>
                    //             <span> {thousand(row.avgThisWeek)}</span>
                    //           </Card.Grid>
                    //         </Card>
                    //         <Card
                    //           type="inner"
                    //           title={t('one_week_ago')}
                    //           className="rounded-xl"
                    //           hoverable
                    //           loading={loading}
                    //         >
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomLeftRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('sales')}:</span>
                    //             <span> {thousand(row.salesOneWeekAgo)}</span>
                    //           </Card.Grid>
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomRightRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('average')}:</span>
                    //             <span> {thousand(row.avgOneWeekAgo)}</span>
                    //           </Card.Grid>
                    //         </Card>
                    //         <Card
                    //           type="inner"
                    //           title={t('two_week_ago')}
                    //           className="rounded-xl"
                    //           hoverable
                    //           loading={loading}
                    //         >
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomLeftRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('sales')}:</span>
                    //             <span> {thousand(row.salesTwoWeekAgo)}</span>
                    //           </Card.Grid>
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomRightRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('average')}:</span>
                    //             <span> {thousand(row.avgTwoWeekAgo)}</span>
                    //           </Card.Grid>
                    //         </Card>
                    //         <Card
                    //           type="inner"
                    //           title={t('three_week_ago')}
                    //           className="rounded-xl"
                    //           hoverable
                    //           loading={loading}
                    //         >
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomLeftRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('sales')}:</span>
                    //             <span> {thousand(row.salesThreeWeekAgo)}</span>
                    //           </Card.Grid>
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomRightRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('average')}:</span>
                    //             <span> {thousand(row.avgThreeWeekAgo)}</span>
                    //           </Card.Grid>
                    //         </Card>
                    //         <Card
                    //           type="inner"
                    //           title={t('four_week_ago')}
                    //           className="rounded-xl"
                    //           hoverable
                    //           loading={loading}
                    //         >
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomLeftRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('sales')}:</span>
                    //             <span> {thousand(row.salesFourWeekAgo)}</span>
                    //           </Card.Grid>
                    //           <Card.Grid
                    //             className="bg-white"
                    //             hoverable={false}
                    //             style={{
                    //               ...bottomRowGridStyle,
                    //               borderBottomRightRadius: '0.75rem'
                    //             }}
                    //           >
                    //             <span className="font-bold">{t('average')}:</span>
                    //             <span> {thousand(row.avgFourWeekAgo)}</span>
                    //           </Card.Grid>
                    //         </Card>
                    //       </div>
                    //     </div>
                    //   ),
                    // }
                  ]}
                />
              </div>
            </Spin>
          )
        }}
      />
    </MainPage>
  );
}

export default CloverSalesSummary;
